import React, { useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';

import useAuthUser from '../lib/auth/useAuthUser';
import { Palette, radius, spacing, useThemePalette } from '../theme';

// Warns the user, when fully signed out, that their data lives only on this
// device and could be lost. Tapping opens the Profile (where they sign in).
// Renders nothing once signed in (real account or anonymous — both sync).
const LoggedOutBanner: React.FC = () => {
  const user = useAuthUser();
  const { t } = useTranslation();
  const router = useRouter();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  if (user) return null;

  return (
    <TouchableOpacity
      style={styles.banner}
      onPress={() => router.push('/profile')}
      activeOpacity={0.85}
      accessibilityLabel={t('auth.signIn')}
    >
      <MaterialCommunityIcons name="alert-outline" size={18} color={palette.danger} />
      <Text style={styles.text}>{t('auth.loggedOutWarning')}</Text>
      <Text style={styles.action}>{t('auth.signIn')}</Text>
    </TouchableOpacity>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    banner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      marginHorizontal: spacing.lg,
      marginBottom: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: p.danger,
      backgroundColor: p.surfaceMuted,
    },
    text: { flex: 1, fontSize: 12.5, color: p.textSecondary },
    action: { fontSize: 13, fontWeight: '700', color: p.secondary },
  });

export default LoggedOutBanner;
